import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import {
  AuthenticatedTemplate,
  useAccount,
  useMsal,
} from "@azure/msal-react";
import RequestInterceptor from "./components/auth/reqInterceptor";
import "./App.scss";
import AppLayout from "./components/layouts/appLayout";
import StyleGuide from "./pages/styleGuide/StyleGuide";
import DataSourceForm from "./components/organisms/dataSourceForm/";
import DataTargetForm from "./components/organisms/dataTargetForm/dataTargetForm";
import SpokeForm from "./components/organisms/spokeForm/spokeForm";
import DataLoaderForm from "./components/organisms/dataLoaderForm/";
import ConfigureTables from "./components/organisms/dataExtractorForm/configureTables";
import ConfigureTablesForm from "./components/organisms/dataExtractorForm/dataExtractorForm";
import EditExtractorForm from "./components/organisms/editExtractorForm/configureExtractor";
import OverrideForm from "./components/organisms/overrideForm/overrideForm";
import ResourceExplorer from "./pages/resourcesExplorer/ResourceExplorer";
import OverRideList from "./pages/overRidesList/overRidesList";
import OverrideRequestList from "./pages/overrideRequestsList/overrideRequestsList";
import DataSourceList from "./pages/dataSourceList/dataSourceList";
import DataTargetList from "./pages/dataTargetList/dataTargetList";
import DataSetList from "./pages/dataSetList/dataSetList";
import DataExtractorList from "./pages/dataExtractorList/dataExtractorList";
import SubscriptionList from "./pages/subscriptionList/subscriptionList";
import SpokeList from "./pages/spokeList/spokeList";
import DataTargetSelectionList from "./pages/dataTargetSelectionList/dataTargetSelectionList";
import DataLoaderList from "./pages/dataLoaderList/dataLoaderList";
import MyAssets from "./pages/myAssets/MyAssets";
import MyApprovals from "./pages/myApprovals/MyApprovals";
import NoMatch from "./components/shared/noMatch";
import useNotifier from "./components/shared/useNotifier";
import { callMsGraph } from "./components/auth/msGraphCall";
import { useDispatch, useSelector } from "react-redux";
import {
  SetAccessToken,
  SetProfileData,
  SetTokenClaims,
  SetRoleClaims,
} from "./store/auth/actions";
import { loginRequest } from "./config/AuthConfig";
import jwt from "jwt-decode";
import { MODE } from "./constants/_pickerValues";
import {
  getProfileDataSelector,
  getRoleClaimsSelector,
} from "./store/auth/selectors";
import {
  getLoadersListSelector,
} from "./store/dataLoader/selectors";
import { ISearchParam } from "./store/dataLoader/types";
import { getTargetListSelector } from "./store/dataTarget/selectors";
import { ITargetListParams } from "./store/dataTarget/types";
import LoginPage from "./pages/loginPage/loginPage";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import Box from "@mui/material/Box/Box";
import * as actions from "./store/userProfile/actions";
import InsituExtractorList from "./pages/insituExtractorList/insituExtractorList";
import InsituExtractorForm from "./components/organisms/insituExtractorForm/InsituExtractorForm";
import OverrideRequestForm from "./components/organisms/overrideRequestForm/overrideRequestForm";

function App() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [profileData, setProfileData] = useState(null);
  const [token, setToken] = useState("");
  const [idtoken, setIdToken] = useState("");
  const [isAdmin, setIsAdmin] = useState(true);
  const [displayIndex, setDisplayIndex] = useState(<></>);
  const [mail, setMail] = useState("")
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const roles = useSelector(getRoleClaimsSelector); // roles  - IDP.Admin or IDP.User
  const loaders = useSelector(getLoadersListSelector);
  const targets = useSelector(getTargetListSelector);
  const profile = useSelector(getProfileDataSelector);

  let decodeTokenClaims: any = "";

  const loaderParams: ISearchParam = {
    skip: 0,
    top: 10,
    count: true,
    search: "",
    owner: undefined,
  };

  const targetParams: ITargetListParams = {
    skip: 0,
    top: 10,
    count: true,
    search: "",
    layerType: undefined,
    owner: undefined,
    endpoint: undefined,
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (account) {
      instance
        .acquireTokenSilent({
          // Can be done when a button clicked
          //Replace with ApiScope from Authonfig.ts
          ...loginRequest,
          account: account,
        })
        .then((response) => {
          if (response) {
            setToken(response.accessToken);
            setIdToken(response.idToken);
            setIsAuthenticated(response.idTokenClaims.hasOwnProperty('roles'));
            callMsGraph(response.accessToken).then((result) =>
              setProfileData(result)
            );
          }
        });
    }
  }, [account, instance]);

  useEffect(() => {
    dispatch(SetAccessToken(token));

    // find out token has role claims and what role the user has - IDP.Admin / IDP.User
    // if Admin show admin pages OR user pages
    // set User Role in global store to get it from any component or write custom hook.
    // show role based content.

    if (idtoken) {
      decodeTokenClaims = jwt(idtoken);
      dispatch(SetTokenClaims(decodeTokenClaims));
      dispatch(SetRoleClaims(decodeTokenClaims.roles));
    }
  }, [dispatch, token, idtoken]);

  useEffect(() => {
    dispatch(SetProfileData(profileData));
  }, [dispatch, profileData]);


  useEffect(() => {
    if (profile) {
      setMail(profile.userPrincipalName)

    }
  }, [profile]);


  useEffect(() => {
    if (mail) {
      dispatch(actions.fetchProfileRequest({ userName: mail }));
    }
  }, [mail]);

  useNotifier();

  useEffect(() => {
    if (roles && roles.length > 0) {
      setIsAdmin(roles.includes("IDP.Admin") ? true : false);
    }
  }, [roles]);

  useEffect(() => {
    if (loaders !== undefined && targets !== undefined) {
      if (targets.count === 0 && loaders.count === 0) {
        setDisplayIndex(<DataSetList />);
      } else {
        setDisplayIndex(<MyAssets />);
      }
    }
  }, [loaders, targets]);

  useEffect(() => {
    if (profile) {
      loaderParams.owner = [profile["userPrincipalName"]];
      targetParams.owner = [profile["userPrincipalName"]];

      // dispatch(fetchLoadersReq(loaderParams));
      // dispatch(fetchTargetListReq(targetParams));
    }
  }, [profile]);

  if (isAuthenticated) {
    return (
      <>
        <AuthenticatedTemplate>
          {profileData && (
            <div className="app">
              <RequestInterceptor>
                <Routes>
                  <Route path="/" element={<AppLayout />}>
                    <Route index={true} element={displayIndex} />
                    <Route path="DataSetList" element={<DataSetList />} />
                    <Route path="MyAssets" element={<MyAssets />} />
                    <Route path="MyApprovals" element={<MyApprovals />} />
                    <Route path="DataSourceList" element={<DataSourceList />} />
                    <Route path="InsituExtractorList" element={<InsituExtractorList />} />
                    <Route path="DataTargetList" element={<DataTargetList />} />
                    <Route path="Subscriptions" element={<SubscriptionList />} />
                    <Route path="ViewInsituExtractor/:batchCode"
                      element={<InsituExtractorForm mode={MODE.view}></InsituExtractorForm>}
                    />

                    {(
                      <Route
                        path="DataExtractorList/:sourceName"
                        element={<DataExtractorList />}
                      />
                    )}
                    <Route
                      path="DataExtractorList"
                      element={<DataExtractorList />}
                    />
                    {(
                      <Route
                        path="DataLoaderList/Target/:targetName"
                        element={<DataLoaderList />}
                      />
                    )}
                    {
                      <Route
                        path="DataLoaderList/ExtractorBatchCode/:extractorBatchCode"
                        element={<DataLoaderList />}
                      />
                    }
                    {isAdmin && (
                      <Route
                        path="DataLoaderList"
                        element={<DataLoaderList />}
                      />
                    )}

                    {isAdmin && (
                      <Route
                        path="AddDataSource"
                        element={<DataSourceForm />}
                      />
                    )}
                    <Route
                      path="ViewDataSource/:sourceId"
                      element={<DataSourceForm mode={MODE.view} />}
                    />
                    {isAdmin && (
                      <Route
                        path="EditDataSource/:sourceId"
                        element={<DataSourceForm mode={MODE.edit} />}
                      />
                    )}
                    <Route path="AddDataLoader" element={<DataLoaderForm />} />
                    <Route
                      path="ViewDataLoader/:batchCode"
                      element={<DataLoaderForm mode={MODE.view} />}
                    />
                    <Route
                      path="EditDataLoader/:batchCode"
                      element={<DataLoaderForm mode={MODE.edit} />}
                    />
                    {isAdmin && (
                      <Route
                        path="AddDataTarget"
                        element={<DataTargetForm />}
                      />
                    )}
                    {isAdmin && (
                      <Route path="AddSpoke" element={<SpokeForm />} />
                    )}
                    <Route
                      path="ViewSpoke/:spokeId"
                      element={<SpokeForm mode={MODE.view} />}
                    />
                    <Route
                      path="ViewDataTarget/:targetId"
                      element={<DataTargetForm mode={MODE.view} />}
                    />
                    <Route
                      path="EditDataTarget/:targetId"
                      element={<DataTargetForm mode={MODE.edit} />}
                    />
                    {isAdmin && (
                      <Route
                        path="ConfigureTables"
                        element={<ConfigureTables />}
                      />
                    )}
                    {isAdmin && (
                      <Route
                        path="ConfigureTablesForm"
                        element={<ConfigureTablesForm />}
                      />
                    )}
                    <Route
                      path="ViewDataExtractor/:batchCode"
                      element={<ConfigureTablesForm mode={MODE.view} />}
                    />
                    <Route
                      path="EditDataExtractor/:batchCode"
                      element={<EditExtractorForm />}
                    />
                    {isAdmin && (
                      <Route
                        path="ResourceExplorer"
                        element={<ResourceExplorer />}
                      />
                    )}
                    {isAdmin && (
                      <Route path="StyleGuide" element={<StyleGuide />} />
                    )}
                    {isAdmin && (
                      <Route path="SpokeList" element={<SpokeList />} />
                    )}
                    {isAdmin && (
                      <Route path="OverrideList" element={<OverRideList />} />
                    )}
                    {isAdmin && (
                      <Route path="AddOverRide" element={<OverrideForm />} />
                    )}
                    {<Route path="NewOverrideRequest" element={<OverrideRequestForm />} />}
                    {<Route path="ViewOverRideRequest/:requestId" element={<OverrideRequestForm mode={MODE.view} />} />}
                    <Route
                      path="ViewOverride/:overrideId"
                      element={<OverrideForm mode={MODE.view} />}
                    />
                    {isAdmin && (
                      <Route
                        path="EditOverride/:overrideId"
                        element={<OverrideForm mode={MODE.edit} />}
                      />
                    )}
                    <Route
                      path="OverrideRequestList"
                      element={<OverrideRequestList />}
                    />
                    {
                      isAdmin && (
                        <Route path="EditInsituExtractor/:batchCode"
                          element={<InsituExtractorForm mode={MODE.edit}></InsituExtractorForm>}
                        />
                      )
                    }
                    {
                      isAdmin && (
                        <Route path="AddInsituCriticality"
                          element={<InsituExtractorForm />}
                        />
                      )
                    }
                    <Route path="SearchData" element={<DataSetList />} />
                    <Route
                      path="SelectTarget"
                      element={<DataTargetSelectionList />}
                    />
                    <Route
                      path="LoginPage"
                      element={<LoginPage instance={instance} />}
                    />
                    <Route path="*" element={<NoMatch />} />
                  </Route>
                </Routes>
              </RequestInterceptor>
            </div>
          )}
        </AuthenticatedTemplate >
      </>
    );
  } else if (inProgress === "login" || inProgress === "handleRedirect") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          paddingTop: "20%",
          paddingLeft: "50%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else {
    return (
      <>
        <LoginPage instance={instance} autoSignOn={false} />
      </>
    );
  }
}

export default App;
